import React, { useState, useEffect } from 'react';
import { Calendar, Edit, Trash, Search, RefreshCw, Plus, Eye } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../../axiosConfig';

const MERCHANT_TYPES = ['Attractions', 'Taxi', 'Train', 'Hotel', 'Tour Operator', 'Tour Guide'];

export default function CompactMerchantManagementPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFromUrl = searchParams.get('type');
  const [activeType, setActiveType] = useState(
    MERCHANT_TYPES.includes(typeFromUrl) ? typeFromUrl : 'Attractions'
  );

  const handleTypeChange = (type) => {
    setActiveType(type);
    setSearchParams({ type }, { replace: true });
  };

  // useState's initial value only runs once on mount, so clicking a
  // different sidebar submenu link while already on this page (same route,
  // just a new ?type= param) wouldn't otherwise update the active tab - only
  // the sidebar's own highlight would move. This keeps them in sync.
  useEffect(() => {
    const urlType = searchParams.get('type');
    if (MERCHANT_TYPES.includes(urlType) && urlType !== activeType) {
      setActiveType(urlType);
    }
  }, [searchParams, activeType]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [merchants, setMerchants] = useState([]);

  // Filter states
  const [merchantName, setMerchantName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [verifiedStatus, setVerifiedStatus] = useState('');

  // Add navigation handler for edit button with debug logging
  const handleEditClick = (entityId) => {
    if (!entityId) {
      console.error('Entity ID is undefined or null');
      return;
    }
    console.log('Navigating to:', `/EditNavigationTabs`);
    navigate(`/EditNavigationTabs`);
  };

    const handleDeleteClick = async (merchant) => {
    const merchantId = merchant?.id; // API expects numeric "id"

    if (!merchantId) {
      console.error('Merchant numeric id is missing. Full merchant:', merchant);
      alert('Cannot delete: merchant id is missing.');
      return;
    }

    const ok = window.confirm(`Are you sure you want to remove "${merchant.name}"?`);
    if (!ok) return;

    try {
      setLoading(true);

      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/entity-panel/config/state-update',
        {
          id: merchantId,
          state: false,
          mode: 'REMOVE',
        }
      );

      // Remove from UI immediately (no re-fetch needed)
      setMerchants((prev) => prev.filter((m) => m.id !== merchantId));

      console.log('Delete response:', response.data);
    } catch (err) {
      console.error('Error deleting merchant:', err);
      alert('Failed to remove merchant. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch merchants data from API (all data at once)
  const fetchMerchants = async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();

      // Add filters to params if they exist
      if (filters.merchantName) {
        params.append('name', filters.merchantName);
      }
      if (filters.entityType) {
        params.append('entityType', filters.entityType);
      }
      // Normalize verified status to boolean string if provided
      if (filters.verifiedStatus !== undefined && filters.verifiedStatus !== '') {
        const verifiedBoolString = String(filters.verifiedStatus === true || filters.verifiedStatus === 'true');
        params.append('isVerified', verifiedBoolString);
      }
      if (filters.fromDate) {
        params.append('fromDate', filters.fromDate);
      }
      if (filters.toDate) {
        params.append('toDate', filters.toDate);
      }

      const response = await axiosInstance.get(
        `/internal-api/v1/client-web/entity-panel/config/list?${params.toString()}`
      );

      const data = response.data;
      if (data && data.data) {
        setMerchants(data.data);
      }
    } catch (err) {
      setError('Failed to fetch merchants. Please try again later.');
      console.error('Error fetching merchants:', err);
      setMerchants([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchMerchants();
  }, []);

  // Handle search
  const handleSearch = () => {
    // Validate date range before searching
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      setError('From date cannot be after To date.');
      return;
    }
    setCurrentPage(1); // Reset to first page when searching
    fetchMerchants({
      merchantName,
      entityType,
      // Pass a normalized value for verified status
      verifiedStatus: verifiedStatus === '' ? '' : verifiedStatus === 'true',
      fromDate,
      toDate
    });
  };

  // Handle reset
  const handleReset = () => {
    setMerchantName('');
    setEntityType('');
    setVerifiedStatus('');
    setFromDate('');
    setToDate('');
    setShowFromCalendar(false);
    setShowToCalendar(false);
    setCurrentPage(1);
    fetchMerchants({});
  };

  // Helper function to format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Generate calendar days for current month
  const generateCalendarDays = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  // Calendar component
  const DatePicker = ({ onSelect, onClose, initialDate }) => {
    const today = new Date();
    const initialDateObj = initialDate ? new Date(initialDate) : today;
    const [currentMonth, setCurrentMonth] = useState(isNaN(initialDateObj.getTime()) ? today.getMonth() : initialDateObj.getMonth());
    const [currentYear, setCurrentYear] = useState(isNaN(initialDateObj.getTime()) ? today.getFullYear() : initialDateObj.getFullYear());

    const days = generateCalendarDays(currentYear, currentMonth);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const prevMonth = () => {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    };

    const nextMonth = () => {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    };

    const handleDateSelect = (day) => {
      if (day) {
        const selectedDate = new Date(currentYear, currentMonth, day);
        onSelect(selectedDate);
        onClose();
      }
    };

    return (
      <div className="absolute left-0 z-10 w-64 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg top-full">
        <div className="flex items-center justify-between p-2 border-b border-slate-200">
          <button onClick={prevMonth} className="text-slate-500 hover:text-slate-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <div className="text-xs font-medium text-slate-700">
            {monthNames[currentMonth]} {currentYear}
          </div>
          <button onClick={nextMonth} className="text-slate-500 hover:text-slate-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
        <div className="p-2">
          <div className="grid grid-cols-7 gap-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="py-1 text-xs font-medium text-center text-slate-500">
                {day}
              </div>
            ))}
            {days.map((day, index) => (
              <div
                key={index}
                onClick={() => handleDateSelect(day)}
                className={`text-center py-1 rounded-full text-xs cursor-pointer ${
                  day === null
                    ? 'text-transparent pointer-events-none'
                    : 'hover:bg-brand-blue/10'
                } ${
                  today.getDate() === day &&
                  today.getMonth() === currentMonth &&
                  today.getFullYear() === currentYear
                    ? 'bg-brand-blue text-white hover:bg-brand-blue-dark'
                    : 'text-slate-700'
                }`}
              >
                {day || '•'}
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-between p-2 border-t border-slate-200">
          <button
            onClick={() => {
              onSelect(today);
              onClose();
            }}
            className="px-3 py-1 text-xs font-medium text-brand-blue transition-colors bg-brand-blue/10 rounded-md hover:bg-brand-blue/20"
          >
            Today
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1 text-xs text-slate-700 transition-colors bg-slate-100 rounded-md hover:bg-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Frontend pagination logic
  const totalItems = merchants.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = merchants.slice(indexOfFirstItem, indexOfLastItem);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    const start = Math.max(2, currentPage - delta);
    const end = Math.min(totalPages - 1, currentPage + delta);

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    if (start > 2) {
      rangeWithDots.push(...range);
    } else {
      rangeWithDots.push(...range.filter((i) => i !== 1));
    }

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1 && !rangeWithDots.includes(totalPages)) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Merchants</h1>
          <div className="mt-1 text-sm text-slate-500">
            Home <span className="mx-1.5 text-slate-400">/</span>
            Merchants <span className="mx-1.5 text-slate-400">/</span>
            <span className="font-semibold text-slate-700">{activeType}</span>
          </div>
        </div>
        <a
          href="/MerchantForm"
          className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg shadow-sm bg-brand-blue hover:bg-brand-blue-dark"
        >
          <Plus size={14} />
          Add New Merchant
        </a>
      </div>

      {/* Type switcher */}
      <div className="inline-flex gap-1 p-1 mb-4 bg-white border rounded-lg border-slate-200">
        {MERCHANT_TYPES.map((type) => (
          <button
            key={type}
            onClick={() => handleTypeChange(type)}
            className={`px-4 h-9 rounded-md text-sm font-semibold transition-colors ${
              activeType === type
                ? 'bg-brand-blue text-white'
                : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      <>
      {/* Filters Card */}
      <div className="p-4 mb-4 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-bold text-slate-900">Filters</h2>

        <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Merchant name</label>
            <div className="relative">
              <Search size={14} className="absolute text-slate-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                placeholder="e.g. Sigiriya Rock Fortress"
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className="w-full py-2.5 pl-9 pr-3 text-sm transition-colors border rounded-lg border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Entity type</label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="w-full px-3 py-2.5 text-sm transition-colors border rounded-lg border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
            >
              <option value="">Entity type</option>
              <option value="Government Entity (Fully Owned)">Government Entity</option>
              <option value="Private Entity (Fully Owned)">Private Entity</option>
            </select>
          </div>

          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Verified status</label>
            <select
              value={verifiedStatus}
              onChange={(e) => setVerifiedStatus(e.target.value)}
              className="w-full px-3 py-2.5 text-sm transition-colors border rounded-lg border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
            >
              <option value="">Verified status</option>
              <option value="true">Verified</option>
              <option value="false">Not Verified</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
          <div className="relative lg:col-span-4">
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">From</label>
            <div className="relative">
              <Calendar size={14} className="absolute text-slate-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                placeholder="Select date"
                value={fromDate ? formatDate(fromDate) : ''}
                readOnly
                onClick={() => setShowFromCalendar(!showFromCalendar)}
                className="w-full py-2.5 pl-9 pr-3 text-sm transition-colors border rounded-lg border-slate-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
              />
            </div>
            {showFromCalendar && (
              <DatePicker
                onSelect={(date) => setFromDate(date.toISOString().split('T')[0])}
                onClose={() => setShowFromCalendar(false)}
                initialDate={fromDate}
              />
            )}
          </div>

          <div className="relative lg:col-span-4">
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">To</label>
            <div className="relative">
              <Calendar size={14} className="absolute text-slate-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                placeholder="Select date"
                value={toDate ? formatDate(toDate) : ''}
                readOnly
                onClick={() => setShowToCalendar(!showToCalendar)}
                className="w-full py-2.5 pl-9 pr-3 text-sm transition-colors border rounded-lg border-slate-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
              />
            </div>
            {showToCalendar && (
              <DatePicker
                onSelect={(date) => setToDate(date.toISOString().split('T')[0])}
                onClose={() => setShowToCalendar(false)}
                initialDate={toDate}
              />
            )}
          </div>

          <div className="flex items-end gap-2 lg:col-span-4">
            <button
              className="flex items-center justify-center flex-1 gap-2 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-60"
              onClick={handleSearch}
              disabled={loading}
            >
              <Search size={14} />
              Search
            </button>
            <button
              className="flex items-center justify-center flex-1 gap-2 py-2.5 text-sm font-semibold text-slate-700 transition-colors bg-white border rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-60"
              onClick={handleReset}
              disabled={loading}
            >
              <RefreshCw size={14} />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Merchants Table Card */}
      <div className="bg-white border rounded-xl border-slate-200">
        <div className="flex flex-col items-center justify-between gap-3 p-4 sm:flex-row">
          <div className="text-sm font-bold text-slate-900">
            {activeType} Merchants
          </div>

          <div className="text-xs text-slate-500">
            Showing {totalItems === 0 ? 0 : indexOfFirstItem + 1} - {Math.min(indexOfLastItem, totalItems)} of {totalItems}
          </div>
        </div>

        {error && (
          <div className="p-3 mx-5 mb-4 text-sm text-red-700 bg-red-50 rounded-lg">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="px-5 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Name</th>
                <th className="px-5 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Entity Type</th>
                <th className="px-5 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Registration</th>
                <th className="px-5 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Created At</th>
                <th className="px-5 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Verified</th>
                <th className="px-5 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw size={16} className="animate-spin" />
                      <span>Loading merchants...</span>
                    </div>
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center text-slate-500">
                    No merchants found
                  </td>
                </tr>
              ) : (
                currentItems.map((merchant) => (
                  <tr key={merchant.uuid} className="transition-colors hover:bg-slate-50">
                    <td className="px-5 py-3.5 border-b border-slate-100">
                      <div className="font-semibold text-slate-900">{merchant.name}</div>
                      <div className="mt-0.5 text-xs text-slate-400">ID: {merchant.uuid}</div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-700 border-b border-slate-100">
                      {merchant.entityType}
                    </td>
                    <td className="px-5 py-3.5 text-slate-700 border-b border-slate-100">
                      {merchant.registration || 'N/A'}
                    </td>
                    <td className="px-5 py-3.5 text-slate-700 border-b border-slate-100">
                      {formatDate(merchant.createdAt)}
                    </td>
                    <td className="px-5 py-3.5 border-b border-slate-100">
                      <span className={`px-2.5 h-[22px] rounded-full text-xs inline-flex items-center font-semibold ${
                        merchant.isVerified
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {merchant.isVerified ? 'Verified' : 'Not Verified'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 border-b border-slate-100">
                      <div className="flex gap-1.5">
                        <button
                          className="flex items-center justify-center w-7 h-7 transition-colors bg-white border rounded-full border-slate-200 text-slate-500 hover:bg-slate-100"
                          onClick={() => navigate(`/MerchantView?uuid=${merchant.uuid}`)}
                          title="View"
                        >
                          <Eye size={13} />
                        </button>
                        <button
                          className="flex items-center justify-center w-7 h-7 text-amber-600 transition-colors bg-white border rounded-full border-slate-200 hover:bg-amber-50"
                          onClick={() => navigate(`/EditNavigationTabs?uuid=${merchant.uuid}`)}
                          title="Edit merchant"
                        >
                          <Edit size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(merchant)}
                          className="flex items-center justify-center w-7 h-7 transition-colors bg-white border rounded-full border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600"
                          title="Remove merchant"
                        >
                          <Trash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination with Items Per Page Selector */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200">
            {/* Page Info */}
            <div className="text-xs text-slate-500">
              Page {currentPage} of {totalPages}
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center space-x-4">
              {/* Items per page selector */}
              <div className="flex items-center space-x-2">
                <span className="text-xs text-slate-500">Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 text-xs bg-white border rounded-md border-slate-200 text-slate-700"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              {/* Prev / Numbers / Next */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-xs border border-slate-200 rounded-md bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Previous
                </button>

                <div className="flex items-center space-x-1">
                  {getPageNumbers().map((page, index) => (
                    <React.Fragment key={index}>
                      {page === '...' ? (
                        <span className="px-2 py-1.5 text-xs text-slate-400">
                          ...
                        </span>
                      ) : (
                        <button
                          onClick={() => goToPage(page)}
                          className={`px-2.5 py-1.5 text-xs border rounded-md min-w-[32px] ${
                            currentPage === page
                              ? 'bg-brand-blue text-white border-brand-blue'
                              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {page}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-xs border border-slate-200 rounded-md bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </>
    </div>
  );
}
